import { Button } from "@/components/ui/button";
import { cn } from "@/components/utils/utils";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { Plus, UserCheck } from "lucide-react";
import { useI18n } from '@/hooks/useI18n';
import { UserRole } from "@/lib/auth/models/auth.model";
import type { TranslationKey } from "@/types/i18n.types";

interface TravelRequestButtonProps {
  travelRequest: DashboardTravelRequest;
  userRole: UserRole | null;
  onClick: (requestId: string) => void;
  onCreateItinerary?: (requestId: string) => void;
  /** Agency e admin: gira la richiesta a un travel advisor */
  onAssignToMember?: (requestId: string) => void;
  variant?: "card" | "table";
  className?: string;
}

/**
 * L'agency può girare a un proprio membro anche una richiesta già assegnata
 * (tipicamente a sé stessa): l'unico limite è che non abbia più senso farlo,
 * cioè richiesta completata o scaduta.
 * L'admin ha la stessa facoltà sulle richieste già assegnate: quando la richiesta
 * è ancora pending il pulsante principale è già "Assegna", quindi l'azione extra
 * sarebbe un doppione.
 */
export const canAssignToAdvisor = (
  travelRequest: DashboardTravelRequest,
  userRole: UserRole | null
): boolean => {
  if (travelRequest.status === "completed" || travelRequest.isExpired) return false;
  if (userRole === "agency") return true;
  if (userRole === "admin") return travelRequest.status !== "pending";
  return false;
};

/**
 * La richiesta e' ancora sul tavolo dell'agenzia: nessun assegnatario, oppure
 * assegnata all'agenzia stessa (il caso "me ne occupo io" con cui nascono le
 * richieste create a mano). In entrambi i casi nessun membro ci sta lavorando, e per
 * l'agenzia e' un promemoria: va girata a qualcuno.
 *
 * Vale solo per l'agency -- e' lei che distribuisce il lavoro -- e solo finche'
 * assegnare ha un senso: su una richiesta completata, rifiutata o scaduta non c'e'
 * piu' niente da girare.
 */
export const needsAgencyAssignment = (
  travelRequest: DashboardTravelRequest,
  userRole: UserRole | null,
  currentUserId: string | null | undefined
): boolean => {
  if (userRole !== "agency") return false;
  if (travelRequest.status === "completed" || travelRequest.status === "rejected") return false;
  if (travelRequest.isExpired) return false;
  const assignedId = travelRequest.assignedAdvisorId;
  return !assignedId || assignedId === currentUserId;
};

/** Etichetta dell'azione di assegnazione: l'admin non assegna "a un membro" ma a un advisor. */
export const getAssignToAdvisorLabel = (
  travelRequest: DashboardTravelRequest,
  userRole: UserRole | null,
  t: (key: TranslationKey) => string
): string => {
  if (userRole === "admin") return t('travelRequest.reassignToAdvisor');
  return travelRequest.assignedAdvisor?.id
    ? t('travelRequest.reassignToMember')
    : t('travelRequest.assignToMember');
};

type ButtonState = 
  | "admin-assign"
  | "admin-view"
  | "completed-view"
  | "expired-view"
  | "designer-create"
  | "designer-accept"
  | "traveler-view";

export const TravelRequestButton = ({
  travelRequest,
  userRole,
  onClick,
  onCreateItinerary,
  onAssignToMember,
  variant = "card",
  className
}: TravelRequestButtonProps) => {
  const { t } = useI18n();
  
  // Determina lo stato del pulsante in base a ruolo e status
  const getButtonState = (): ButtonState => {
    // Priorità massima: controlla prima il ruolo dell'utente
    if (userRole === "admin") {
      return travelRequest.status === "pending" && !travelRequest.isExpired
        ? "admin-assign"
        : "admin-view";
    }
    if (userRole === "traveler") return "traveler-view";
    
    // Logic per travel_designer
    if (travelRequest.status === "completed") return "completed-view";
    
    // Check se la richiesta è scaduta (pending da più di 48 ore)
    if (travelRequest.isExpired) return "expired-view";
    
    const isAssigned = !!travelRequest.assignedAdvisor?.id && travelRequest.status === "assigned";
    return isAssigned ? "designer-create" : "designer-accept";
  };

  const buttonState = getButtonState();

  // Handler del click basato sullo stato
  const handleClick = () => {
    if (buttonState === "designer-create" && onCreateItinerary) {
      onCreateItinerary(travelRequest.id);
    } else {
      onClick(travelRequest.id);
    }
  };

  // Configurazione del pulsante basata sullo stato
  const getButtonConfig = () => {
    switch (buttonState) {
      case "admin-assign":
        return {
          text: t('travelRequest.assign'),
          variant: "default" as const,
          showIcon: false
        };

      case "admin-view":
        return {
          text: t('travelRequest.viewRequest'),
          variant: "secondary" as const,
          showIcon: false
        };
      
      case "completed-view":
        return {
          text: `${t('common.view')} ${t('travelRequest.requestDetails')}`,
          variant: "secondary" as const,
          showIcon: false
        };
      
      case "expired-view":
        return {
          text: t('travelRequest.viewRequest'),
          variant: "outline" as const,
          showIcon: false
        };
      
      case "designer-create":
        return {
          text: t('itinerary.createItinerary'),
          variant: "default" as const,
          showIcon: true
        };
      
      case "designer-accept":
        return {
          text: t('travelRequest.acceptOrDecline'),
          variant: travelRequest.isPending ? "secondary" as const : "outline" as const,
          showIcon: false
        };
      
      case "traveler-view":
        return {
          text: t('travelRequest.viewRequest'),
          variant: "secondary" as const,
          showIcon: false
        };
    }
  };

  const config = getButtonConfig();

  // Agency e admin vedono un'azione in più per girare la richiesta a un advisor,
  // anche quando è già assegnata (a sé stessa o a un altro advisor).
  const showAssignToMember =
    !!onAssignToMember && canAssignToAdvisor(travelRequest, userRole);

  const assignToMemberLabel = getAssignToAdvisorLabel(travelRequest, userRole, t);

  // Rendering basato sulla variante (card o table)
  if (variant === "card") {
    const mainButton =
      buttonState === "designer-create" ? (
        <>
          <Button
            variant="default"
            className={cn("w-full h-12 rounded-xl", className)}
            onClick={() => onCreateItinerary && onCreateItinerary(travelRequest.id)}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('itinerary.createItinerary')}
          </Button>
          <Button
            variant="secondary"
            className={cn("w-full h-10 rounded-xl", className)}
            onClick={() => onClick(travelRequest.id)}
          >
            {t('travelRequest.viewRequest')}
          </Button>
        </>
      ) : (
        <Button
          variant={config.variant}
          className={cn("w-full h-12 rounded-xl", className)}
          onClick={handleClick}
        >
          {config.showIcon && <Plus className="h-4 w-4 mr-2" />}
          {config.text}
        </Button>
      );

    if (!showAssignToMember) {
      return buttonState === "designer-create" ? (
        <div className="flex flex-col gap-2">{mainButton}</div>
      ) : (
        mainButton
      );
    }

    return (
      <div className="flex flex-col gap-2">
        {mainButton}
        <Button
          variant="outline"
          className={cn("w-full h-10 rounded-xl", className)}
          onClick={() => onAssignToMember(travelRequest.id)}
        >
          <UserCheck className="h-4 w-4 mr-2" />
          {assignToMemberLabel}
        </Button>
      </div>
    );
  }

  // Table variant
  const tableMainButton = (
    <Button
      variant={config.variant}
      size="sm"
      onClick={handleClick}
      className={cn("text-sm", className)}
    >
      {config.showIcon && <Plus className="h-3 w-3 mr-1" />}
      {config.text}
    </Button>
  );

  if (!showAssignToMember) return tableMainButton;

  return (
    <div className="flex items-center gap-2">
      {tableMainButton}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onAssignToMember(travelRequest.id)}
        className={cn("text-sm", className)}
      >
        <UserCheck className="h-3 w-3 mr-1" />
        {assignToMemberLabel}
      </Button>
    </div>
  );
};